import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { BlockDataForm, BlocksForm } from '@plone/volto/components/manage/Form';
import { SidebarPortal } from '@plone/volto/components';
import config from '@plone/volto/registry';
import EditBlockWrapper from '@plone/volto/components/manage/Blocks/Block/EditBlockWrapper';
import BlockChooserButton from '@plone/volto/components/manage/BlockChooser/BlockChooserButton';
import Schema from './schema';
import { limparAncora } from './abrirPorAncora';

/**
 * Accordion Edit v5 — gerenciamento de itens no CORPO, não na sidebar.
 * Sidebar: eyebrow + title + openFirst + abrirTodas.
 *
 * data.items = [{ id, question, resumo?, ancora?, blocks, blocks_layout }]
 *
 * v5.6: dois campos opcionais por item, "Resumo" e "Âncora", no painel do item
 * aberto (o campo da pergunta só existe durante a renomeação e some no blur,
 * então não serve de lugar para eles). A âncora é gravada sem # e sem espaços.
 * v5.5: acumulador `blockState` no onChangeField (duas chamadas seguidas
 * do volto-slate ao colar vários parágrafos apagavam a resposta).
 * v5.4b: clique em qualquer vão da caixa (fora de bloco interno) dá foco —
 * antes só o clique na própria caixa contava, e o vão do formulário
 * aninhado engolia a maioria dos cliques.
 * v5.4: pedido de foco explícito {itemId, subBlockId, reqId}, consumido só
 * quando o editor certo está montado e selecionado; alvo = 1º bloco de texto
 * da resposta; não mexe no cursor se o foco já está lá; confirmação da
 * pergunta idempotente (revisão externa de 10/09/2026).
 * v5.3: focusAnswer leva o cursor de fato ao editor da resposta (foco no DOM
 * do slate). O volto-slate só foca sozinho quando o bloco passa de não
 * selecionado para selecionado; se ele já estava selecionado, Enter na
 * pergunta e clique na área vazia da caixa deixavam o foco no body e o
 * texto digitado se perdia (verificado no dev em 10/09/2026).
 * v5.2: botão "+ Bloco" na resposta (seletor de blocos do Volto) e o
 * placeholder do slate escondido enquanto o placeholder da resposta aparece.
 * v5.1: Enter/Escape do campo da pergunta não sobem ao formulário principal
 * (com blockHasOwnFocusManagement no registro do bloco, ver index.js).
 * v5: a área da resposta é visível e rotulada ("Resposta"), com placeholder
 * quando vazia e selo "sem resposta" no item. Os formatos antigos suportados
 * pelo View ({ question, answer } e o aninhado data.data) são convertidos em
 * blocos ao normalizar, nunca descartados.
 */

// Texto de um value do Slate (recursivo).
const slateText = (v) => {
  if (Array.isArray(v)) return v.map(slateText).join('');
  if (v && typeof v === 'object') {
    return (v.text || '') + slateText(v.children || []);
  }
  return '';
};

// Item tem resposta: algum bloco não-slate, ou algum slate com texto.
const itemHasAnswer = (it) => {
  const ids = it?.blocks_layout?.items || [];
  return ids.some((id) => {
    const b = it.blocks?.[id];
    if (!b) return false;
    if (b['@type'] !== 'slate') return true;
    return slateText(b.value).trim().length > 0;
  });
};

// Cada item precisa de >=1 sub-block (slate) para o BlocksForm aninhado
// (isMainForm=false) ser editável: não há botão "+" e o ENTER do Volto só
// cria block novo a partir de um block existente.
const makeSlate = (text) => {
  const sid = uuid();
  const block = { '@type': 'slate' };
  if (text) {
    block.value = [{ type: 'p', children: [{ text }] }];
    block.plaintext = text;
  }
  return { blocks: { [sid]: block }, blocks_layout: { items: [sid] } };
};

const normalizeItems = (data) => {
  let items = Array.isArray(data?.items) ? data.items : [];

  // Formato aninhado legado (data.data = { blocks, blocks_layout }), o mesmo
  // que o View usa quando items[] está vazio.
  if (items.length === 0) {
    const nested = data?.data;
    const ids = nested?.blocks_layout?.items || [];
    items = ids.map((pid) => {
      const nb = nested.blocks?.[pid] || {};
      return {
        id: pid,
        question: nb.title || '',
        blocks: nb.blocks,
        blocks_layout: nb.blocks_layout,
      };
    });
  }

  return items.map((it) => {
    const hasContent =
      it.blocks_layout &&
      Array.isArray(it.blocks_layout.items) &&
      it.blocks_layout.items.length > 0;
    // Formato { question, answer } legado: a resposta vira um slate com o
    // mesmo texto (o View prefere blocks a answer; sem isto ela sumiria).
    const answer = typeof it.answer === 'string' ? it.answer.trim() : '';
    const fallback = hasContent ? null : makeSlate(answer);
    return {
      ...it,
      id: it.id || uuid(),
      question: it.question || '',
      blocks: hasContent ? it.blocks : fallback.blocks,
      blocks_layout: hasContent ? it.blocks_layout : fallback.blocks_layout,
    };
  });
};

// Botão do seletor de blocos da resposta (substitui o ⊕ padrão do Volto).
const AddBlockButton = ({ onShowBlockChooser }) => (
  <button
    type="button"
    className="ag-accordion__add-block-btn"
    title="Adicionar um bloco à resposta"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onShowBlockChooser();
    }}
  >
    + Bloco{' '}
    <span className="ag-accordion__add-block-hint">imagem, vídeo, HTML…</span>
  </button>
);

const AccordionEdit = (props) => {
  const {
    block,
    data,
    onChangeBlock,
    selected,
    pathname,
    metadata,
    properties,
    manage,
  } = props;

  const normalizedItems = useMemo(
    () => normalizeItems(data),
    [data?.items, data?.data], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [openIdx, setOpenIdx] = useState(0);
  const [renameIdx, setRenameIdx] = useState(-1);
  const [selectedSubBlock, setSelectedSubBlock] = useState(null);

  const persist = (newItems) => {
    onChangeBlock(block, { ...data, items: newItems });
  };

  const firstBlockId = (it) => it?.blocks_layout?.items?.[0] || null;

  // Caixa da resposta de cada item (por id), para focar o editor no DOM.
  const answerRefs = useRef({});

  // Pedido de foco pendente: { itemId, subBlockId, reqId }. Só é entregue
  // quando o item está aberto e o sub-bloco pedido está selecionado; se o
  // usuário mudou de item ou de bloco antes, o pedido é descartado.
  const focusReq = useRef(null);
  const reqCounter = useRef(0);

  // Confirmação da pergunta feita por Enter/Escape: o blur que vier depois
  // (o input desmonta) não grava de novo nem desfaz o cancelamento.
  const renameDone = useRef(false);

  // 1º bloco de texto (slate) da resposta — o único que recebe cursor.
  const firstSlateId = (it) =>
    (it?.blocks_layout?.items || []).find(
      (id) => it.blocks?.[id]?.['@type'] === 'slate',
    ) || null;

  // Entrega o foco pedido, se o editor certo já está montado e selecionado.
  const deliverFocus = () => {
    const req = focusReq.current;
    if (!req) return;
    const idx = normalizedItems.findIndex((x) => x.id === req.itemId);
    if (idx !== openIdx || selectedSubBlock !== req.subBlockId) {
      focusReq.current = null; // pedido superado por outra interação
      return;
    }
    const body = answerRefs.current[req.itemId];
    const editor = body?.querySelector(
      '.block.slate.selected [data-slate-editor="true"]',
    );
    if (!editor) return; // ainda não montou; tenta no próximo render
    focusReq.current = null;
    if (editor.contains(document.activeElement)) return; // já está lá
    editor.focus();
    try {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (e) {
      // sem seleção programática: o foco já basta para digitar
    }
  };

  // Após cada render: entrega o pedido; se o editor ainda não montou, uma
  // única tentativa no próximo frame, cancelada se houver novo render.
  useEffect(() => {
    deliverFocus();
    if (!focusReq.current) return undefined;
    const raf = window.requestAnimationFrame(() => deliverFocus());
    return () => window.cancelAnimationFrame(raf);
  });

  // Leva o cursor para a resposta do item: abre o item, seleciona o 1º bloco
  // de texto e registra o pedido de foco. Resposta só com mídia: seleciona o
  // 1º bloco (sidebar dele) e não há cursor a dar.
  const focusAnswer = (idx, it) => {
    setOpenIdx(idx);
    const target = firstSlateId(it);
    if (!target) {
      setSelectedSubBlock(firstBlockId(it));
      focusReq.current = null;
      return;
    }
    setSelectedSubBlock(target);
    reqCounter.current += 1;
    focusReq.current = {
      itemId: it.id,
      subBlockId: target,
      reqId: reqCounter.current,
    };
  };

  const addItem = () => {
    const seeded = makeSlate('');
    persist([
      ...normalizedItems,
      {
        id: uuid(),
        question: '',
        blocks: seeded.blocks,
        blocks_layout: seeded.blocks_layout,
      },
    ]);
    setOpenIdx(normalizedItems.length);
    setRenameIdx(normalizedItems.length);
  };

  const removeItem = (idx) => {
    if (!window.confirm('Remover este item e todo o conteúdo dele?')) return;
    persist(normalizedItems.filter((_, i) => i !== idx));
    if (openIdx >= normalizedItems.length - 1) setOpenIdx(-1);
  };

  const renameItem = (idx, newQ) => {
    const newItems = [...normalizedItems];
    newItems[idx] = { ...newItems[idx], question: newQ };
    persist(newItems);
  };

  const moveItem = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= normalizedItems.length) return;
    const newItems = [...normalizedItems];
    [newItems[idx], newItems[j]] = [newItems[j], newItems[idx]];
    persist(newItems);
  };

  const updateItemContent = (idx, patch) => {
    const newItems = [...normalizedItems];
    newItems[idx] = { ...newItems[idx], ...patch };
    persist(newItems);
  };

  // Bloco escolhido no seletor entra no fim da resposta e fica selecionado.
  const insertBlockInAnswer = (idx, it, value) => {
    const nid = uuid();
    updateItemContent(idx, {
      blocks: { ...it.blocks, [nid]: value },
      blocks_layout: { items: [...(it.blocks_layout?.items || []), nid] },
    });
    setOpenIdx(idx);
    setSelectedSubBlock(nid);
  };

  // Resumo e âncora do item: opcionais, gravados direto no item.
  const gravarResumo = (idx, valor) =>
    updateItemContent(idx, { resumo: valor });
  const gravarAncora = (idx, valor) =>
    updateItemContent(idx, { ancora: limparAncora(valor) });

  // Enter e Escape nos campos do item não sobem ao formulário principal
  // (criariam um bloco fora do acordeão), como no campo da pergunta.
  const naoSubirTecla = (e) => {
    if (e.key === 'Enter') e.preventDefault();
    if (e.key === 'Enter' || e.key === 'Escape') e.stopPropagation();
  };

  const schema = Schema();

  // Acumulador por render para gravações em duas chamadas seguidas
  // (`onChangeField('blocks', …)` e `onChangeField('blocks_layout', …)`),
  // como faz o volto-slate ao dividir texto colado em vários blocos. Sem
  // isto a segunda chamada partia do estado antigo e apagava a primeira —
  // a resposta inteira sumia ao colar vários parágrafos. Mesmo padrão do
  // Container/Edit.jsx do Volto.
  const blockState = {};

  return (
    <>
      <section className="ag-accordion ag-accordion--edit">
        {data.eyebrow && (
          <span className="ag-accordion__eyebrow">{data.eyebrow}</span>
        )}
        {data.title && <h2 className="ag-accordion__heading">{data.title}</h2>}

        {normalizedItems.length === 0 ? (
          <p className="ag-accordion__empty">
            Clique em <em>+ Item</em> abaixo para começar.
          </p>
        ) : (
          <div className="ag-accordion__list">
            {normalizedItems.map((it, i) => {
              const isOpen = i === openIdx;
              const hasAnswer = itemHasAnswer(it);
              const editingAnswer =
                selected &&
                !!selectedSubBlock &&
                (it.blocks_layout?.items || []).includes(selectedSubBlock);
              return (
                <div
                  key={it.id}
                  className={`accordion__item ag-accordion__item${
                    isOpen ? ' open' : ''
                  }`}
                  data-open={isOpen ? 'true' : 'false'}
                >
                  <div className="ag-accordion__row">
                    {renameIdx === i ? (
                      <input
                        type="text"
                        className="ag-accordion__rename-input"
                        autoFocus
                        defaultValue={it.question}
                        placeholder="Escreva a pergunta e pressione Enter"
                        onFocus={() => {
                          renameDone.current = false;
                        }}
                        onBlur={(e) => {
                          if (renameDone.current) return; // já confirmado
                          renameItem(i, e.target.value || `Item ${i + 1}`);
                          setRenameIdx(-1);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            // Não deixar o Enter subir ao formulário
                            // principal (criaria um bloco fora do acordeão).
                            e.preventDefault();
                            e.stopPropagation();
                            renameDone.current = true;
                            renameItem(i, e.target.value || `Item ${i + 1}`);
                            setRenameIdx(-1);
                            focusAnswer(i, it);
                          }
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            renameDone.current = true; // cancela sem gravar
                            setRenameIdx(-1);
                          }
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="accordion__trigger ag-accordion__trigger ag-accordion__trigger--edit"
                        onClick={() => setOpenIdx(isOpen ? -1 : i)}
                        onDoubleClick={() => setRenameIdx(i)}
                        title="Clique para abrir, duplo-clique para renomear"
                        aria-expanded={isOpen}
                      >
                        <span className="ag-accordion__trigger-text">
                          {it.question || `Item ${i + 1}`}
                        </span>
                        {!hasAnswer && (
                          <span className="ag-accordion__badge">
                            sem resposta
                          </span>
                        )}
                      </button>
                    )}
                    <div className="ag-accordion__row-actions">
                      <button
                        type="button"
                        onClick={() => setRenameIdx(i)}
                        title="Renomear"
                        aria-label="Renomear"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem(i, -1)}
                        disabled={i === 0}
                        title="Mover acima"
                        aria-label="Mover acima"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem(i, 1)}
                        disabled={i === normalizedItems.length - 1}
                        title="Mover abaixo"
                        aria-label="Mover abaixo"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(i)}
                        title="Remover"
                        aria-label="Remover"
                        className="ag-accordion__row-action--danger"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="accordion__panel ag-accordion__panel ag-accordion__panel--edit">
                      <div className="ag-accordion__campos">
                        <label className="ag-accordion__campo">
                          <span className="ag-accordion__campo-rotulo">
                            Resumo (opcional)
                          </span>
                          <input
                            type="text"
                            className="ag-accordion__campo-input"
                            value={it.resumo || ''}
                            onChange={(e) => gravarResumo(i, e.target.value)}
                            onKeyDown={naoSubirTecla}
                          />
                        </label>
                        <label className="ag-accordion__campo">
                          <span className="ag-accordion__campo-rotulo">
                            Âncora (opcional, ex.: nsd)
                          </span>
                          <input
                            type="text"
                            className="ag-accordion__campo-input"
                            value={it.ancora || ''}
                            onChange={(e) => gravarAncora(i, e.target.value)}
                            onKeyDown={naoSubirTecla}
                          />
                        </label>
                      </div>
                      <div className="ag-accordion__answer">
                        <span className="ag-accordion__answer-label">
                          Resposta
                        </span>
                        {/* Clique na área em branco (fora do bloco) seleciona
                            o 1º bloco da resposta. */}
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                        <div
                          className={`ag-accordion__answer-body${
                            !hasAnswer && !editingAnswer
                              ? ' ag-accordion__answer-body--empty'
                              : ''
                          }`}
                          ref={(el) => {
                            answerRefs.current[it.id] = el;
                          }}
                          onClick={(e) => {
                            // Clique no vão da caixa (fora de qualquer bloco
                            // interno) leva o cursor à resposta. Clique dentro
                            // de um bloco fica com o Volto/slate.
                            const inner = e.target.closest('.block');
                            if (!inner || !e.currentTarget.contains(inner)) {
                              focusAnswer(i, it);
                            }
                          }}
                        >
                          {!hasAnswer && !editingAnswer && (
                            <p
                              className="ag-accordion__answer-placeholder"
                              aria-hidden="true"
                            >
                              Clique aqui e escreva a resposta. Enter cria um
                              novo parágrafo. Para imagem, vídeo ou HTML, use o
                              botão + Bloco.
                            </p>
                          )}
                          <BlocksForm
                            // Na edição, `properties` é a página; sem
                            // `metadata`, um bloco de dentro do item (a
                            // Galeria) não sabe o caminho.
                            metadata={metadata || properties}
                            properties={{
                              blocks: it.blocks,
                              blocks_layout: it.blocks_layout,
                            }}
                            manage={manage}
                            selectedBlock={selected ? selectedSubBlock : null}
                            blocksConfig={config.blocks.blocksConfig}
                            isMainForm={false}
                            stopPropagation={selectedSubBlock}
                            onSelectBlock={(id) => setSelectedSubBlock(id)}
                            onChangeFormData={(newFormData) => {
                              updateItemContent(i, {
                                blocks: newFormData.blocks,
                                blocks_layout: newFormData.blocks_layout,
                              });
                            }}
                            onChangeField={(id, value) => {
                              if (id === 'blocks' || id === 'blocks_layout') {
                                blockState[id] = value;
                                updateItemContent(i, { ...blockState });
                              }
                            }}
                            pathname={pathname}
                          >
                            {({ draginfo }, editBlock, blockProps) => (
                              <EditBlockWrapper
                                draginfo={draginfo}
                                blockProps={blockProps}
                              >
                                {editBlock}
                              </EditBlockWrapper>
                            )}
                          </BlocksForm>
                        </div>
                        <div className="ag-accordion__answer-tools">
                          <BlockChooserButton
                            // `data` só decide se o botão aparece
                            // (blockHasValue): um slate vazio sempre mostra.
                            data={{ '@type': 'slate' }}
                            block={firstBlockId(it)}
                            buttonComponent={AddBlockButton}
                            blocksConfig={config.blocks.blocksConfig}
                            properties={{
                              blocks: it.blocks,
                              blocks_layout: it.blocks_layout,
                            }}
                            onInsertBlock={(id, value) =>
                              insertBlockInAnswer(i, it, value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          className="ag-accordion__add-btn"
          onClick={addItem}
          title="Adicionar item"
        >
          + Item
        </button>
      </section>

      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          onChangeField={(id, value) => {
            onChangeBlock(block, { ...data, [id]: value });
          }}
          formData={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
};

AccordionEdit.propTypes = {
  block: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  pathname: PropTypes.string,
  metadata: PropTypes.object,
  properties: PropTypes.object,
  manage: PropTypes.bool,
};

export default AccordionEdit;
